import React, { useState, useEffect, useRef } from 'react';
import { Dumbbell } from 'lucide-react';
import { normalizeExerciseMediaUrl } from '../lib/exerciseMedia';

/**
 * StaticExerciseImage — renders the first frame of a GIF on a <canvas>.
 * Canvas.drawImage naturally freezes on the first frame (no GIF animation).
 * Uses a global cache to avoid re-loading images on re-renders.
 * Uses IntersectionObserver for lazy loading — only loads when visible.
 */

// Global cache: src → HTMLImageElement (persists across mounts)
const _imgCache = new Map();

export default function StaticExerciseImage({ src: rawSrc, alt = '', className = 'w-full h-full object-cover', size = 'sm' }) {
  // Self-heal dead exercisedb.dev URLs (from cached/snapshotted data) → Cloudinary
  const src = normalizeExerciseMediaUrl(rawSrc);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(!src);
  const [loaded, setLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Lazy visibility detection — only start loading when in viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // If already cached, skip observer — draw immediately
    if (src && _imgCache.has(src)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  // Load and draw image once visible
  useEffect(() => {
    if (!src || !isVisible) return;
    setFailed(false);
    setLoaded(false);

    const canvas = canvasRef.current;
    if (!canvas) return;

    // If cached, draw immediately — no blink
    const cached = _imgCache.get(src);
    if (cached) {
      canvas.width = cached.naturalWidth || cached.width;
      canvas.height = cached.naturalHeight || cached.height;
      canvas.getContext('2d').drawImage(cached, 0, 0);
      setLoaded(true);
      return;
    }

    // Load fresh
    const img = new Image();
    img.onload = () => {
      _imgCache.set(src, img);
      const c = canvasRef.current;
      if (!c) return;
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext('2d').drawImage(img, 0, 0);
      setLoaded(true);
    };
    img.onerror = () => setFailed(true);
    img.src = src;
  }, [src, isVisible]);

  if (failed || !src) {
    const iconSize = size === 'lg' ? 'h-8 w-8' : size === 'md' ? 'h-6 w-6' : 'h-4 w-4';
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted rounded">
        <Dumbbell className={`${iconSize} text-muted-foreground`} />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full relative">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-800 rounded animate-pulse">
          <Dumbbell className="h-4 w-4 text-neutral-600" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={className}
        style={{ opacity: loaded ? 1 : 0 }}
        role="img"
        aria-label={alt}
      />
    </div>
  );
}
